/**
 * Servicio de caché para URLs de imágenes de S3
 * Maneja el almacenamiento local de URLs con expiración de 3 días
 */

const IMAGE_CACHE_KEY = "darpha_image_cache";
const CACHE_EXPIRY_DAYS = 3;

class ImageCacheService {
  constructor() {
    this.cache = this.loadCache();
    this.pendingRequests = new Map(); // Almacenar promesas en vuelo
  }

  /**
   * Carga el caché desde localStorage
   */
  loadCache() {
    try {
      const cached = localStorage.getItem(IMAGE_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error("Error cargando caché de imágenes:", error);
      return {};
    }
  }

  /**
   * Guarda el caché en localStorage
   */
  saveCache() {
    try {
      localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.error("Error guardando caché de imágenes:", error);
    }
  }

  /**
   * Genera una clave única para la imagen
   */
  generateCacheKey(imagePath) {
    return `img_${imagePath.replace(/[/\\]/g, "_")}`;
  }

  /**
   * Verifica si una URL en caché sigue siendo válida.
   * Usa tiempo transcurrido (relativo) para ser inmune a desfases de reloj.
   */
  isUrlValid(cacheEntry) {
    if (!cacheEntry || !cacheEntry.url || !cacheEntry.timestamp) {
      return false;
    }

    const elapsed = Date.now() - cacheEntry.timestamp;
    const maxAge =
      cacheEntry.urlMaxAgeMs || CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const buffer = 5 * 60 * 1000; // 5 minutos de margen

    return elapsed < maxAge - buffer;
  }

  /**
   * Obtiene una URL del caché si está disponible y válida
   */
  getFromCache(imagePath) {
    if (!imagePath) return null;

    const cacheKey = this.generateCacheKey(imagePath);
    const cacheEntry = this.cache[cacheKey];

    if (this.isUrlValid(cacheEntry)) {
      return cacheEntry.url;
    }

    // Si la entrada existe pero ha expirado, la eliminamos
    if (cacheEntry) {
      delete this.cache[cacheKey];
      this.saveCache();
    }

    return null;
  }

  /**
   * Almacena una URL en el caché
   */
  setInCache(imagePath, url) {
    if (!imagePath || !url) return;

    const cacheKey = this.generateCacheKey(imagePath);

    // Verificar si ya existe y es la misma URL para evitar logs duplicados
    const existing = this.cache[cacheKey];
    if (existing && existing.url === url && this.isUrlValid(existing)) {
      return;
    }

    // Parsear expiración real de URL presignada de S3
    let urlMaxAgeMs = null;
    try {
      const urlObj = new URL(url);
      const amzExpires = urlObj.searchParams.get("X-Amz-Expires");
      if (amzExpires) {
        urlMaxAgeMs = Number(amzExpires) * 1000;
      }
    } catch (e) {
      /* URL no-S3, usar TTL por defecto */
    }

    this.cache[cacheKey] = {
      url,
      timestamp: Date.now(),
      urlMaxAgeMs,
      imagePath,
    };

    this.saveCache();
  }

  /**
   * Verifica si hay una petición en vuelo para esta imagen
   */
  isPendingRequest(imagePath) {
    return this.pendingRequests.has(imagePath);
  }

  /**
   * Almacena una promesa en vuelo
   */
  setPendingRequest(imagePath, promise) {
    // Limpiar cualquier petición pendiente previa
    if (this.pendingRequests.has(imagePath)) {
    }

    this.pendingRequests.set(imagePath, promise);

    // Limpiar la promesa cuando termine (éxito o error)
    promise.finally(() => {
      this.pendingRequests.delete(imagePath);
    });

    return promise;
  }

  /**
   * Obtiene una promesa en vuelo
   */
  getPendingRequest(imagePath) {
    return this.pendingRequests.get(imagePath);
  }

  /**
   * Fuerza la limpieza de una petición pendiente específica
   */
  clearPendingRequest(imagePath) {
    if (this.pendingRequests.has(imagePath)) {
      this.pendingRequests.delete(imagePath);
      return true;
    }
    return false;
  }

  /**
   * Elimina una entrada específica del caché
   */
  removeFromCache(imagePath) {
    if (!imagePath) return;

    const cacheKey = this.generateCacheKey(imagePath);

    if (this.cache[cacheKey]) {
      delete this.cache[cacheKey];
      this.saveCache();
    }
  }

  /**
   * Limpia URLs expiradas del caché
   */
  cleanExpiredEntries() {
    let cleanedCount = 0;

    Object.keys(this.cache).forEach((key) => {
      const entry = this.cache[key];
      if (!this.isUrlValid(entry)) {
        delete this.cache[key];
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      this.saveCache();
    }

    return cleanedCount;
  }

  /**
   * Limpia todo el caché
   */
  clearAllCache() {
    this.cache = {};
    this.saveCache();
  }

  /**
   * Obtiene estadísticas del caché
   */
  getCacheStats() {
    const totalEntries = Object.keys(this.cache).length;
    let validEntries = 0;
    let expiredEntries = 0;

    Object.values(this.cache).forEach((entry) => {
      if (this.isUrlValid(entry)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return {
      total: totalEntries,
      valid: validEntries,
      expired: expiredEntries,
      cacheSize: this.calculateCacheSize(),
    };
  }

  /**
   * Calcula el tamaño aproximado del caché en KB
   */
  calculateCacheSize() {
    try {
      const cacheString = JSON.stringify(this.cache);
      return Math.round((cacheString.length / 1024) * 100) / 100; // KB con 2 decimales
    } catch (error) {
      return 0;
    }
  }

  /**
   * Verifica si el caché necesita mantenimiento
   */
  needsMaintenance() {
    const stats = this.getCacheStats();
    return stats.expired > 0 || stats.total > 100; // Más de 100 entradas o hay expiradas
  }

  /**
   * Ejecuta mantenimiento automático del caché
   */
  performMaintenance() {
    const cleaned = this.cleanExpiredEntries();
    const stats = this.getCacheStats();

    // Si aún hay muchas entradas después de limpiar expiradas
    if (stats.total > 150) {
      // Eliminar las más antiguas (estrategia LRU simple)
      const entries = Object.entries(this.cache)
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => a.timestamp - b.timestamp);

      const toRemove = entries.slice(0, 50); // Remover las 50 más antiguas

      toRemove.forEach((entry) => {
        delete this.cache[entry.key];
      });

      this.saveCache();
    }

    return { cleaned, maintained: true };
  }
}

// Instancia singleton del servicio de caché
const imageCacheService = new ImageCacheService();

// Ejecutar mantenimiento al inicializar si es necesario
if (imageCacheService.needsMaintenance()) {
  imageCacheService.performMaintenance();
}

export default imageCacheService;
